/**
 * Infinite scroll for tabbed-collection-scroll.
 * Fetches: /collections/{handle}?page=N&section_id={section.id}
 * Partial HTML is rendered when request.page_type == 'collection' (Liquid).
 */

/**
 * @param {HTMLElement} sectionRoot
 */
function initTabbedScrollTabs(sectionRoot) {
  const tabList = sectionRoot.querySelector('[data-tabbed-scroll-tabs]');
  if (!tabList || tabList.dataset.tabsBound === 'true') return;

  tabList.dataset.tabsBound = 'true';
  tabList.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-tab-target]');
    if (!tab || !tabList.contains(tab)) return;

    const targetId = tab.dataset.tabTarget;
    if (!targetId) return;

    tabList.querySelectorAll('[data-tab-target]').forEach((item) => {
      const selected = item === tab;
      item.classList.toggle('active', selected);
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    sectionRoot.querySelectorAll('.tab-content').forEach((panel) => {
      panel.classList.toggle('active', panel.id === targetId);
    });
  });
}

function initTabbedCollectionScroll(sectionRoot) {
  initTabbedScrollTabs(sectionRoot);

  const sectionId = sectionRoot.dataset.sectionId;
  const panels = sectionRoot.querySelectorAll('[data-tabbed-scroll-panel]');

  panels.forEach((panel) => {
    const grid = panel.querySelector('[data-tabbed-scroll-grid]');
    const trigger = panel.querySelector('[data-tabbed-infinite-scroll-trigger]');

    if (!grid || !trigger) return;

    if (panel._tabbedScrollObserver) {
      panel._tabbedScrollObserver.disconnect();
    }

    panel.dataset.scrollInitialized = 'true';
    let loading = false;

    const collectionHandle =
      panel.closest('[data-collection-handle]')?.dataset.collectionHandle || '';

    const observer = new IntersectionObserver(
      async (entries) => {
        if (!entries[0]?.isIntersecting || loading) return;

        let nextUrl = trigger.dataset.nextUrl;
        if (!nextUrl) return;

        try {
          const fetchUrl = new URL(nextUrl, window.location.origin);
          if (!fetchUrl.searchParams.get('page')) {
            const gridPage = parseInt(grid.dataset.currentPage || '1', 10);
            fetchUrl.searchParams.set('page', String(gridPage + 1));
          }
          if (sectionId && !fetchUrl.searchParams.get('section_id')) {
            fetchUrl.searchParams.set('section_id', sectionId);
          }
          nextUrl = fetchUrl.toString();
        } catch {
          /* use nextUrl as-is */
        }

        loading = true;
        trigger.setAttribute('data-loading', 'true');
        observer.unobserve(trigger);

        try {
          const response = await fetch(nextUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');

          let pageRoot = null;

          if (collectionHandle) {
            pageRoot = doc.querySelector(`[data-tabbed-scroll-page][data-collection-handle="${collectionHandle}"]`);
          }

          if (!pageRoot) {
            pageRoot = doc.querySelector('[data-tabbed-scroll-page]');
          }

          if (!pageRoot && sectionId) {
            const sectionWrapper = doc.getElementById(`shopify-section-${sectionId}`);
            if (collectionHandle && sectionWrapper) {
              pageRoot =
                sectionWrapper.querySelector(`[data-collection-handle="${collectionHandle}"] [data-tabbed-scroll-page]`) ||
                sectionWrapper.querySelector(`[data-collection-handle="${collectionHandle}"]`);
            }
            pageRoot = pageRoot || sectionWrapper?.querySelector('[data-tabbed-scroll-page]') || null;
          }

          if (!pageRoot) {
            pageRoot = doc.querySelector('.shopify-section [data-tabbed-scroll-page]');
          }

          if (!pageRoot) {
            console.warn('[tabbed-collection-scroll] No product markup in response');
            return;
          }

          const newItems = pageRoot.querySelectorAll('[data-tabbed-scroll-item]');

          if (newItems.length === 0) {
            console.warn('[tabbed-collection-scroll] Response had 0 product items');
            trigger.remove();
            return;
          }

          const existingIds = new Set(
            [...grid.querySelectorAll('[data-tabbed-scroll-item]')].map((el) => el.dataset.productId)
          );

          newItems.forEach((item) => {
            const id = item.dataset.productId;
            if (id && existingIds.has(id)) return;
            grid.appendChild(item);
            if (id) existingIds.add(id);
          });

          const newTrigger = pageRoot.querySelector('[data-tabbed-infinite-scroll-trigger]');

          if (newTrigger?.dataset.nextUrl) {
            trigger.dataset.nextUrl = newTrigger.dataset.nextUrl;
            const loadedPage = new URL(newTrigger.dataset.nextUrl, window.location.origin).searchParams.get('page');
            if (loadedPage) {
              grid.dataset.currentPage = loadedPage;
            }
            observer.observe(trigger);
          } else {
            trigger.remove();
          }

          if (typeof ReloadSmartWishlist === 'function') {
            ReloadSmartWishlist();
          }

          if (typeof window.initProductCards === 'function') {
            window.initProductCards();
          }
        } catch (error) {
          console.error('[tabbed-collection-scroll] Infinite scroll error:', error);
          observer.observe(trigger);
        } finally {
          loading = false;
          trigger.removeAttribute('data-loading');
        }
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );

    observer.observe(trigger);
    panel._tabbedScrollObserver = observer;
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-tabbed-collection-scroll]').forEach(initTabbedCollectionScroll);
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const sectionRoot =
    target.matches('[data-tabbed-collection-scroll]')
      ? target
      : target.querySelector('[data-tabbed-collection-scroll]');

  if (sectionRoot) initTabbedCollectionScroll(sectionRoot);
});

if (typeof window !== 'undefined') {
  window.initTabbedCollectionScroll = initTabbedCollectionScroll;
}
